/**
 * Interaction / Dialogue Textbox — DOS-terminal readout, Pokemon-style queue.
 *
 * One docked box. Successive queueTextbox() calls play back-to-back:
 * each LMB press either fast-forwards the typewriter reveal or, once
 * fully revealed, pops the next entry from the queue.
 *
 * Entries pause gameplay by default. Pass `environmental: true` (or
 * `pausesGame: false`) for future ambient readouts that should not freeze
 * the world.
 */

import { playTextboxType } from "../systems/audio.js";

export const TEXTBOX_CONFIG = {
    variants: {
        system: [51, 255, 140],
        memory: [255, 193, 77],
        locked: [255, 92, 92],
        flashback: [180, 140, 255],
    },
    defaultVariant: "system",
    charInterval: 0.028, // seconds per revealed character
    cursorBlinkInterval: 0.4,
    outerMargin: 0.06, // fraction of screen width, each side
    bottomMargin: 0.2, // fraction of screen height, up from the bottom edge
    panelHeight: 108,
    z: 10100, // above every other layer (memoryUI's z(10000) is currently the next highest)
};

let queue = [];
let current = null; // { tag, channel, text, variant, pausesGame, revealed, timer, typing }
let initialized = false;

let bodyTextNode = null;
let footTextNode = null;
let lastAdvanceInputAt = -Infinity;

const TEXTBOX_INPUT_CONSUME_SECONDS = 0.08;

function variantColor(variant) {
    return TEXTBOX_CONFIG.variants[variant] || TEXTBOX_CONFIG.variants[TEXTBOX_CONFIG.defaultVariant];
}

function entryPausesGame(entry) {
    if (!entry) return true;
    if (entry.pausesGame === false || entry.environmental === true) return false;
    return true;
}

function computeLayout() {
    const w = width();
    const h = height();
    const marginX = w * TEXTBOX_CONFIG.outerMargin;
    const panelW = w - marginX * 2;
    const panelH = TEXTBOX_CONFIG.panelHeight;
    const x = marginX;
    const y = h - panelH - h * TEXTBOX_CONFIG.bottomMargin;
    return { x, y, panelW, panelH };
}

function bodyDisplayText() {
    if (!current) return "";
    const revealedStr = current.text.slice(0, current.revealed);
    if (!current.typing) return "> " + revealedStr;
    return "> " + revealedStr + (current.cursorOn ? "▎" : " ");
}

function refreshBodyText() {
    if (!bodyTextNode || !bodyTextNode.exists() || !current) return;
    bodyTextNode.text = bodyDisplayText();
    bodyTextNode.color = rgb(...variantColor(current.variant));
    if (footTextNode && footTextNode.exists()) {
        footTextNode.hidden = current.typing;
    }
}

function buildShell(entry) {
    destroyAll("textbox_ui");
    bodyTextNode = null;
    footTextNode = null;

    const { x, y, panelW, panelH } = computeLayout();
    const accent = variantColor(entry.variant);
    const zBase = TEXTBOX_CONFIG.z;

    // Hard offset shadow (no blur — pixel-flat)
    add([
        rect(panelW, panelH),
        pos(x + 4, y + 4),
        color(0, 0, 0),
        opacity(0.8),
        fixed(),
        z(zBase - 1),
        "textbox_ui",
    ]);

    // Panel fill + single ruled border
    add([
        rect(panelW, panelH),
        pos(x, y),
        color(0, 0, 0),
        outline(3, rgb(...accent)),
        fixed(),
        z(zBase),
        "textbox_ui",
    ]);

    bodyTextNode = add([
        text("", { font: "ShareTechMono", size: 14, width: panelW - 40 }),
        pos(x + 16, y + 14),
        anchor("topleft"),
        color(...accent),
        fixed(),
        z(zBase + 1),
        "textbox_ui",
    ]);

    footTextNode = add([
        text("▸ continue  LMB", { font: "ShareTechMono", size: 10 }),
        pos(x + panelW - 12, y + panelH - 10),
        anchor("botright"),
        color(...accent),
        opacity(0.85),
        fixed(),
        z(zBase + 1),
        "textbox_ui",
        { baseX: x + panelW - 12 },
    ]);

    refreshBodyText();
}

function startNext() {
    if (queue.length === 0) {
        current = null;
        destroyAll("textbox_ui");
        bodyTextNode = null;
        footTextNode = null;
        return;
    }
    const entry = queue.shift();
    current = {
        tag: entry.tag,
        channel: entry.channel || "",
        text: entry.text,
        variant: entry.variant || TEXTBOX_CONFIG.defaultVariant,
        pausesGame: entryPausesGame(entry),
        revealed: 0,
        timer: 0,
        typing: true,
        cursorOn: true,
        cursorTimer: 0,
        onClose: entry.onClose,
    };
    buildShell(current);
    if (typeof entry.onShow === "function") {
        entry.onShow();
    }
}

function advance() {
    if (!current) return;
    if (current.typing) {
        current.revealed = current.text.length;
        current.typing = false;
        refreshBodyText();
    } else {
        const onClose = current.onClose;
        startNext();
        if (typeof onClose === "function") onClose();
    }
}

export function isTextboxOpen() {
    return !!current;
}

/** True when the active textbox freezes gameplay (default). Environmental tips skip this. */
export function isTextboxPausing() {
    return !!current && current.pausesGame !== false;
}

export function isTextboxAdvanceInputConsumed() {
    return time() - lastAdvanceInputAt <= TEXTBOX_INPUT_CONSUME_SECONDS;
}

/** Screen-space bounds of the docked panel, for the lighting overlay to punch through. */
export function getTextboxBounds() {
    if (!current) return null;
    const { x, y, panelW, panelH } = computeLayout();
    return { x, y, w: panelW, h: panelH };
}

export function queueTextbox(entry) {
    queue.push(entry);
    if (!current) startNext();
}

export function queueTextboxSequence(entries) {
    entries.forEach((entry) => queue.push(entry));
    if (!current) startNext();
}

export function closeTextbox() {
    queue = [];
    current = null;
    destroyAll("textbox_ui");
    bodyTextNode = null;
    footTextNode = null;
}

/**
 * Every scene() entry gets a fresh KAPLAY event-listener scope — call this
 * before initTextboxUI() on each "game" scene start so the typewriter
 * onUpdate and advance onMousePress get re-registered, not skipped as
 * already-initialized from a previous boot/scene lifetime.
 */
export function resetTextboxUIForScene() {
    closeTextbox();
    initialized = false;
}

export function initTextboxUI() {
    if (initialized) return;
    initialized = true;

    onMousePress("left", () => {
        if (current) {
            lastAdvanceInputAt = time();
            advance();
        }
    });

    onResize(() => {
        if (current) buildShell(current);
    });

    onUpdate(() => {
        if (!current) return;

        if (current.typing) {
            current.timer += dt();
            const targetRevealed = Math.min(
                Math.floor(current.timer / TEXTBOX_CONFIG.charInterval),
                current.text.length
            );
            if (targetRevealed !== current.revealed) {
                const prev = current.revealed;
                current.revealed = targetRevealed;
                // One click per newly revealed character (spaces stay quiet)
                for (let i = prev; i < targetRevealed; i++) {
                    const ch = current.text[i];
                    if (ch && ch !== " " && ch !== "\n") {
                        playTextboxType();
                        break; // one beep per frame even if interval caught up
                    }
                }
                if (current.revealed >= current.text.length) {
                    current.typing = false;
                }
                refreshBodyText();
            }
        }

        current.cursorTimer += dt();
        if (current.cursorTimer >= TEXTBOX_CONFIG.cursorBlinkInterval) {
            current.cursorTimer = 0;
            current.cursorOn = !current.cursorOn;
            if (current.typing) refreshBodyText();
        }

        if (footTextNode && footTextNode.exists() && !footTextNode.hidden) {
            const nudge = Math.floor(time() * 3) % 2 === 0 ? 0 : 3;
            footTextNode.pos = vec2(footTextNode.baseX - nudge, footTextNode.pos.y);
        }
    });
}
